import { Ingredient, SearchIngredient } from '@/api/types'
import { faEye, faEyeSlash, faTags } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Cell,
  Column,
  CompactTable,
  Row,
  Table,
  TableBody,
  TableHeader,
  TablePagination,
  TableSearch,
} from '@/components/Table'
import './IngredientsTable.css'
import { Button } from '@/components/Button'

const routeApi = getRouteApi('/ingredients/fcdb')

type Props = {
  ingredients: SearchIngredient[]
  totalCount: number
  handleToggleVisibility: (ingredientId: number, state: boolean) => void
}

export default function IngredientsTable({
  ingredients,
  totalCount,
  handleToggleVisibility,
}: Props) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const searchParams = routeApi.useSearch()

  const handleSearch = (text: string) => {
    navigate({
      to: '/ingredients/fcdb',
      search: {
        ...searchParams,
        search: text,
        page: '0',
      },
    })
  }

  return (
    <div className="IngredientIndexTable">
      <div className="IngredientIndexTable-header">
        <TableSearch handleSearch={handleSearch} defaultValue={searchParams.search} />
      </div>

      <Table aria-label="Ingredients table">
        <TableHeader dependencies={[searchParams.page]}>
          <Column id="visibility" width="30"></Column>
          <Column id="name">{t('ingredient.name')}</Column>
          <Column id="englishName">{t('ingredient.englishName')}</Column>
          <Column id="ingredientGroup">{t('ingredient.ingredientGroup')}</Column>
          <Column id="idIngredient">FCDB ID</Column>
        </TableHeader>
        <TableBody items={ingredients}>
          {(ingredient) => (
            <Row id={ingredient.idIngredient} key={ingredient.idIngredient}>
              <Cell>
                <Button
                  variant="icon"
                  onPress={() =>
                    handleToggleVisibility(ingredient.idIngredient, !ingredient.hideFromUser)
                  }
                  aria-label={ingredient.hideFromUser ? 'Show ingredient' : 'Hide ingredient'}>
                  <FontAwesomeIcon
                    opacity={ingredient.hideFromUser ? 0.4 : 1}
                    icon={ingredient.hideFromUser ? faEyeSlash : faEye}
                  />
                </Button>
              </Cell>
              <Cell>
                <Link
                  to="/ingredients/fcdb/$ingId"
                  params={{ ingId: ingredient.idIngredient.toString() }}
                  className="IngredientCell-link">
                  {ingredient.name}
                </Link>
              </Cell>
              <Cell>{ingredient.englishName}</Cell>
              <Cell>{ingredient.ingredientGroup?.name}</Cell>
              <Cell>{ingredient.idIngredient}</Cell>
            </Row>
          )}
        </TableBody>
      </Table>
      <TablePagination
        pagination={{
          totalCount: totalCount,
          page: searchParams.page ? parseInt(searchParams.page, 10) : 0,
          setPage: (page) => {
            navigate({
              to: '/ingredients/fcdb',
              search: {
                ...searchParams,
                page: page.toString(),
              },
            })
          },
        }}
      />
    </div>
  )
}
